import React, { useCallback, useState, useRef, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useMemory } from "../features/memory/hooks/useMemory";
import { AddCommentForm } from "../features/memory/components/AddCommentForm";
import { CommentList } from "../features/memory/components/CommentsList";
import { LikedButton } from "../features/memory/components/LikedButton";
import CommentIconImg from "../assets/comment_icon.svg";
import { FavoriteButton } from "../features/memory/components/FavoriteButton";
import { FavoriteMessageTooltip } from "../features/memory/components/FavoriteMessageTooltip";
import { toFormatString } from "../libs/helper";
import { VideoList } from "../components/VideoList";
import { AudioMemoryItem } from "../features/memory/components/AudioMemoryItem";
import { getDaysToReply } from "../features/memory/libs/memoryHelper";
import { AnswererProfileImage } from "../components/AnswererProfileImage";
import { useSearchParams } from "react-router-dom";
import {
    PartialLinkText,
    SpinAnimation,
    ImageList,
} from "@twioku/common_components";
import { AddQuestionLink } from "../components/AddQuestionLink";
import { Path } from "../routes/PagePath";
import { useAppState } from "../stores/appContext";
import { useFavoriteGuideTooltip } from "../features/memory/hooks/useFavoriteGuideTooltip";
import { useFavoriteSavedTooltip } from "../features/memory/hooks/useFavoriteSavedTooltip";

type NavigationState = { willComment: boolean };

export const MemoryDetail: React.FC = () => {
    const { cognitoUsername } = useAppState();
    const { id } = useParams();
    const {
        memory,
        newCommentText,
        newCommentImages,
        setNewCommentText,
        addComment,
        addFavorite,
        removeFavorite,
        incrementLikedCount,
        setCommentImages,
        tempLikedMemoryIds,
        isLoadingAddComment,
        isMemoryLoading,
        isFavoriteLoading,
    } = useMemory(id ? id : "");
    const location = useLocation();
    const state = location.state as NavigationState;
    const [audioPlayed, setAudioPlayed] = useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const targetCommentId = searchParams.get("commentId");
    const [willComment, setWillComment] = useState<boolean>(false);
    const commentRef = useRef<HTMLInputElement | null>(null);
    const favoriteButtonRef = useRef<HTMLButtonElement | null>(null);
    const { isGuideOpen, closeGuide } = useFavoriteGuideTooltip({
        userId: cognitoUsername ?? "",
    });
    const { isSavedOpen, openSaved, closeSaved } = useFavoriteSavedTooltip();

    const closeFavoriteTooltip = useCallback(() => {
        closeSaved();
        closeGuide();
    }, [closeGuide, closeSaved]);

    useEffect(() => {
        if (!commentRef?.current) return;

        if (state?.willComment || willComment) {
            commentRef.current.focus();
            setWillComment(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state?.willComment, willComment, commentRef.current]);

    const scrollRef = useCallback(
        (element: HTMLDivElement | null) => {
            if (element != null && state?.willComment) {
                element.scrollIntoView({
                    behavior: "smooth",
                    block: "end",
                });
            }
        },
        [state?.willComment]
    );

    const handleClickSendCommentButton = (
        e: React.MouseEvent<HTMLButtonElement>
    ) => {
        e.preventDefault();
        addComment();
    };

    const handleClickLikedButton = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        incrementLikedCount();
    };

    const handleClickFavoriteButton = async (
        e: React.MouseEvent<HTMLButtonElement>
    ) => {
        e.preventDefault();
        if (memory.isFavorite) {
            closeFavoriteTooltip();
            removeFavorite();
            return;
        }
        const addFavoriteResult = await addFavorite();
        if (addFavoriteResult !== "success") return;

        closeGuide();
        openSaved();
    };

    const handleClickQuoteReply = (text: string) => {
        setNewCommentText(text);
        setWillComment(true);
    };

    const daysToReplay = (): string => {
        if (!memory.respondDate || !memory.sendDate) return "";

        const respondDate = new Date(memory.respondDate);
        const sendDate = new Date(memory.sendDate);
        return getDaysToReply(respondDate, sendDate);
    };

    if (!memory || isMemoryLoading)
        return (
            <div className="absolute inset-0 flex items-center justify-center">
                <SpinAnimation />
            </div>
        );

    return (
        <main className="mx-auto w-full max-w-5xl" ref={scrollRef}>
            <div className="flex w-full max-w-5xl">
                <div className="mx-4 flex-1 pb-48">
                    <article className="mb-4 mt-2">
                        <section>
                            <header>
                                <div className="flex items-center justify-between">
                                    <div className="flex">
                                        <AnswererProfileImage
                                            src={
                                                memory.answererProfileImagePath
                                            }
                                            imageSize="medium"
                                            answererId={memory.answererId}
                                        />
                                        <div className="ml-2 flex items-center">
                                            <p className="text-sm">
                                                {memory.answererName}
                                            </p>
                                        </div>
                                    </div>
                                    <FavoriteButton
                                        ref={favoriteButtonRef}
                                        isFavorited={memory.isFavorite}
                                        disabled={isFavoriteLoading}
                                        handleClick={handleClickFavoriteButton}
                                    />
                                    <FavoriteMessageTooltip
                                        open={isSavedOpen || isGuideOpen}
                                        anchorEl={favoriteButtonRef.current}
                                        message={
                                            isSavedOpen
                                                ? "栞を挟みました。栞を挟んだ記憶はこちら"
                                                : "後から見返したい記憶は、栞で区別できます。"
                                        }
                                        placement="bottom"
                                        to={
                                            isSavedOpen
                                                ? Path.favoriteMemories
                                                : undefined
                                        }
                                        autoHideMs={5000}
                                        onClose={closeFavoriteTooltip}
                                    />
                                </div>
                                <h2 className="mt-6 border-l-4 border-primary px-3 text-sm">
                                    {memory.title}
                                </h2>
                            </header>
                            <div
                                data-testid="memory-respond-content"
                                className="contents"
                            >
                                {memory.memoryVoiceAudioPath && (
                                    <AudioMemoryItem
                                        profileImagePath={
                                            memory.answererProfileImagePath
                                        }
                                        voiceAudioPath={
                                            memory.memoryVoiceAudioPath
                                        }
                                        onPlay={() => setAudioPlayed(true)}
                                    />
                                )}
                                {memory.memoryVoiceAudioPath ? (
                                    <p
                                        className={`mx-2 whitespace-pre-line p-1 text-gray-400 ${
                                            audioPlayed
                                                ? "animate-fadeInText"
                                                : ""
                                        }`}
                                    >
                                        {audioPlayed && memory.respondContent}
                                    </p>
                                ) : (
                                    <PartialLinkText className="ml-4 whitespace-pre-line break-all pt-3">
                                        {memory.respondContent}
                                    </PartialLinkText>
                                )}
                                <ImageList
                                    srcList={memory.memoryImagePaths ?? []}
                                />
                                <VideoList srcList={memory.memoryVideoPaths} />
                            </div>
                        </section>
                    </article>
                    <div className="mb-2 ml-4 flex items-center justify-between">
                        <div>
                            <p className="mb-1 text-sm text-gray-800">
                                {memory?.respondDate
                                    ? toFormatString(
                                          new Date(memory.respondDate),
                                          false,
                                          "/"
                                      )
                                    : ""}
                            </p>
                            <p className="text-xs">{daysToReplay()}</p>
                        </div>
                        <div className="flex items-center">
                            <img
                                src={CommentIconImg}
                                className="w-6"
                                alt="comment icon"
                            />
                            <div className="ml-2 text-gray-800">
                                {memory.comments.length}
                            </div>
                            <div className="ml-2">
                                <LikedButton
                                    likedCount={memory.likedCount}
                                    disabled={tempLikedMemoryIds.includes(
                                        memory.id
                                    )}
                                    handleClick={handleClickLikedButton}
                                />
                            </div>
                        </div>
                    </div>
                    <hr className="mb-3" />
                    <CommentList
                        comments={memory.comments}
                        answererId={memory.answererId}
                        activeCommentId={targetCommentId}
                        handleClickQuoteReply={handleClickQuoteReply}
                    />
                </div>
                <div className="fixed bottom-0 left-0 w-full bg-white">
                    <div className="mx-auto mb-4 flex max-w-5xl justify-end text-right">
                        <AddQuestionLink answererId={memory.answererId}>
                            聞いてみたい質問が思い浮かびましたか？
                            <br />
                            こちらから質問を追加してみましょう！
                        </AddQuestionLink>
                    </div>
                    <div className="mx-auto max-w-5xl">
                        <AddCommentForm
                            commentText={newCommentText}
                            imageFiles={newCommentImages}
                            handleChangeCommentText={(e) =>
                                setNewCommentText(e.target.value)
                            }
                            handleClickSendCommentButton={
                                handleClickSendCommentButton
                            }
                            handleChangeAddImageButton={setCommentImages}
                            placeholder="コメントを入力"
                            disabled={isLoadingAddComment}
                            ref={commentRef}
                        />
                    </div>
                </div>
            </div>
        </main>
    );
};
